import React, { createContext, useContext, useState, useCallback } from 'react';
import { TaskCreateModalProps } from '../../../components/TaskCreateModal';
import { PdfViewModalProps } from '../../../components/PdfViewModal/PdfViewModal';
import { PayFlowModalProps } from '../../modals/PayFlow/PayModal';
import { ConfirmationDialogProps } from '../../../components/ConfirmationDlg/ConfirmationDlg';
import { TaskSignersModalProps } from '../../../pages/TaskDetail/TaskSignersModal';
import { SignModalProps } from '../../modals/SignFlow/SignModal';
import { SettingsModalProps } from '../../modals/Settings/SettingModal';
import { AddPaymentMethodModalProps } from '../../../components/AddPaymentMethodModal/AddPaymentMethodModal';
import { ContactCreateModalProps } from '../../../pages/ContactCreateModal/ContactCreateModal';
import { VerificationDlgProps } from '../../../pages/VerificationDlg/VerificationDlg';
import { ContactDetailUpdateMOdalProps } from '../../../pages/ContactCreateModal/ContactDetailUpdateModal';
import { BulkImportModalProps } from '../../../pages/BulkImport/BulkImport';
import { LinkEntityModalProps } from '../ClientDetailTabs/ClientProfileTab/components/LinkEntityModal/LinkEntityModal';
import { BillCreateFormData } from '../../providers/TaskCreationProvider/TaskCreationProvider';
import { Task, TemplateType } from '@admiin-com/ds-graphql';
import { DocumentCreateModalProps } from '../../modals/DocumentCreate/DocumentCreateModal';
import { AddClientOwnerModalProps } from '../../modals/AddClientOwner/AddClientOwnerModal';
export type ModalType =
  | 'taskCreation'
  | 'paymentSubmitted'
  | 'signFlow'
  | 'payFlow'
  | 'viewDocument'
  | 'searchMobile'
  | 'confirmDialog'
  | 'taskSigners'
  | 'settings'
  | 'entityCreate'
  | 'clientCreate'
  | 'entityVerification'
  | 'addPaymentMethod'
  | 'contactCreate'
  | 'contactDetail'
  | 'bulkImport'
  | 'serviceDetail'
  | 'linkEntity'
  | 'documentCreate'
  | 'documentPreview'
  | 'taskReview'
  | 'addClientOwner';

export type ConfirmDialogProps = Omit<
  ConfirmationDialogProps,
  'open' | 'onClose'
>;
export type TaskSignersProps = Omit<TaskSignersModalProps, 'open' | 'onClose'>;
export type TaskCreateProps = Omit<TaskCreateModalProps, 'open' | 'onClose'>;
export type PayFlowProps = Omit<PayFlowModalProps, 'open' | 'onClose'>;
export type PdfViewProps = Omit<PdfViewModalProps, 'open' | 'onClose'>;
export type SignFlowProps = Omit<SignModalProps, 'open' | 'onClose'>;
export type SettingsProps = Omit<SettingsModalProps, 'open' | 'onClose'>;
export type EntityVerificationProps = Omit<
  VerificationDlgProps,
  'open' | 'onClose'
>;
export type AddPaymentMethodProps = Omit<
  AddPaymentMethodModalProps,
  'open' | 'handleClose'
>;
export type ContactCreateProps = Omit<
  ContactCreateModalProps,
  'open' | 'handleCloseModal'
>;
export type ContactDetailProps = Omit<
  ContactDetailUpdateMOdalProps,
  'open' | 'handleCloseModal'
>;
export type BulkImportProps = Omit<
  BulkImportModalProps,
  'open' | 'handleCloseModal'
>;
export type LinkEntityProps = Omit<LinkEntityModalProps, 'open' | 'onClose'>;

export type ServiceDetailProps = {
  service: any;
};

export type DocumentCreateProps = Omit<
  DocumentCreateModalProps,
  'open' | 'onClose'
>;

export type DocumentPreviewProps = {
  templateHtml?: string | null;
  taskCreationContext?: BillCreateFormData;
  includeLetterhead?: boolean | null;
  templateType?: TemplateType;
};

export type TaskReviewProps = {
  task: Task;
  onSuccess?: () => void;
  onEdit?: () => void;
};

export type AddClientOwnerProps = Omit<
  AddClientOwnerModalProps,
  'open' | 'onClose'
>;

export type ModalProps =
  | TaskCreateProps
  | SignFlowProps
  | PayFlowProps
  | PdfViewProps
  | ConfirmDialogProps
  | TaskSignersProps
  | SettingsProps
  | EntityVerificationProps
  | AddPaymentMethodProps
  | ContactCreateProps
  | ContactDetailProps
  | ServiceDetailProps
  | DocumentCreateProps
  | DocumentPreviewProps
  | SettingsProps
  | LinkEntityProps
  | BulkImportProps
  | DocumentCreateProps
  | TaskReviewProps;

export interface Modal {
  id: string;
  type: ModalType;
  isOpen: boolean;
  props?: ModalProps;
}

interface ModalContextType {
  modals: Modal[];
  openModal: (type: ModalType, props?: ModalProps) => string;
  closeModal: (id: string) => void;
  closeAllModals: () => void;
}

const ModalContext = createContext<ModalContextType | undefined>(undefined);

export function ModalProvider({ children }: { children: React.ReactNode }) {
  const [modals, setModals] = useState<Modal[]>([]);

  const openModal = useCallback((type: ModalType, props?: ModalProps) => {
    const id = Math.random().toString(36).substr(2, 9);
    setModals((prev) => [...prev, { id, type, isOpen: true, props }]);
    return id;
  }, []);

  const closeModal = useCallback((id: string) => {
    setModals((prev) => prev.filter((modal) => modal.id !== id));
  }, []);

  const closeAllModals = useCallback(() => {
    setModals([]);
  }, []);

  return (
    <ModalContext.Provider
      value={{ modals, openModal, closeModal, closeAllModals }}
    >
      {children}
    </ModalContext.Provider>
  );
}

export function useModal() {
  const context = useContext(ModalContext);
  if (context === undefined) {
    throw new Error('useModal must be used within a ModalProvider');
  }
  return context;
}
